(function () {
    const DURATION = 0.45;

    function number(value) {
        return Number.parseInt(String(value || '').replace(/,/g, ''), 10) || 0;
    }

    function formatNumber(value) {
        return Math.round(value).toLocaleString('en-US');
    }

    function stateValue(element, previous) {
        return number(previous ? element.dataset.previous : element.dataset.current);
    }

    function connectorPath(anchor, target, index, position) {
        const horizontalY = 19 - (index * 3);

        if (position === 'bottom') {
            return 'M ' + anchor + ' 30 V ' + (30 - horizontalY) + ' H ' + target + ' V 0';
        }

        return 'M ' + anchor + ' 0 V ' + horizontalY + ' H ' + target + ' V 30';
    }

    function labelAnchor(label, connector) {
        const connectorLayer = connector?.closest('.compare-chart__connectors');
        if (!connectorLayer) {
            return number(label.dataset.compareAnchor);
        }

        const labelRect = label.getBoundingClientRect();
        const connectorRect = connectorLayer.getBoundingClientRect();

        if (!connectorRect.width) {
            return number(label.dataset.compareAnchor);
        }

        const anchor = ((labelRect.left + (labelRect.width / 2) - connectorRect.left) / connectorRect.width) * 100;
        return Math.min(100, Math.max(0, anchor));
    }

    function setNumber(element, value, animate) {
        const from = number(element.textContent);

        if (!animate || !window.gsap) {
            element.textContent = formatNumber(value);
            return;
        }

        window.gsap.to({value: from}, {
            value: value,
            duration: DURATION,
            ease: 'power2.inOut',
            onUpdate: function () {
                element.textContent = formatNumber(this.targets()[0].value);
            }
        });
    }

    function setChartState(chart, previous, animate) {
        const segments = Array.from(chart.querySelectorAll('.compare-chart__segment'));
        const values = segments.map(function (segment) {
            return stateValue(segment, previous);
        });
        const graphValues = values.map(function (value) {
            return Math.abs(value);
        });
        const total = graphValues.reduce(function (sum, value) {
            return sum + value;
        }, 0) || 1;

        let offset = 0;

        segments.forEach(function (segment, index) {
            const width = (graphValues[index] / total) * 100;
            const midpoint = offset + (width / 2);

            if (animate && window.gsap) {
                window.gsap.to(segment, {
                    '--segment-width': width + '%',
                    duration: DURATION,
                    ease: 'power2.inOut'
                });
            } else {
                segment.style.setProperty('--segment-width', width + '%');
            }

            const label = chart.querySelector('[data-compare-label="' + index + '"]');
            if (label) {
                const maxConnectorPosition = index === segments.length - 1 ? 99 : 96;
                const clampedMidpoint = Math.min(maxConnectorPosition, Math.max(4, midpoint));
                const connector = chart.querySelector('[data-compare-connector="' + index + '"]');

                if (connector) {
                    const anchor = labelAnchor(label, connector);
                    const path = connectorPath(
                        anchor,
                        clampedMidpoint,
                        index,
                        connector.dataset.compareConnectorPosition
                    );

                    if (animate && window.gsap) {
                        window.gsap.to(connector, {
                            attr: {d: path},
                            duration: DURATION,
                            ease: 'power2.inOut'
                        });
                    } else {
                        connector.setAttribute('d', path);
                    }
                }

                const valueElement = label.querySelector('[data-current][data-previous]');
                if (valueElement) {
                    setNumber(valueElement, values[index], animate);
                }
            }

            offset += width;
        });

        chart.querySelectorAll('[data-compare-summary]').forEach(function (summary) {
            const scope = summary.dataset.compareSummary;
            const count = scope === 'expenditure' ? 3 : segments.length;
            const summaryValue = values.slice(0, count).reduce(function (sum, value) {
                return sum + value;
            }, 0);
            setNumber(summary, summaryValue, animate);
        });

        chart.querySelectorAll('.compare-chart__summary--budget [data-current][data-previous]').forEach(function (budget) {
            setNumber(budget, stateValue(budget, previous), animate);
        });

        chart.querySelectorAll('.compare-chart__accepted [data-current][data-previous]').forEach(function (accepted) {
            setNumber(accepted, stateValue(accepted, previous), animate);
        });
    }

    function chartRevealElements(chart) {
        return chart.querySelectorAll('.compare-chart__labels, .compare-chart__summary');
    }

    function prepareChartReveal(chart) {
        const bar = chart.querySelector('.compare-chart__bar');
        const fill = chart.querySelector('.compare-chart__bar-fill');

        if (!bar || !fill || !window.gsap) {
            return;
        }

        window.gsap.set(bar, {
            '--outline-height': '0%',
            '--outline-width': '0%'
        });
        window.gsap.set(fill, {clipPath: 'inset(0 100% 0 0)'});
        window.gsap.set(chartRevealElements(chart), {opacity: 0});
    }

    function revealChart(chart, delay) {
        const bar = chart.querySelector('.compare-chart__bar');
        const fill = chart.querySelector('.compare-chart__bar-fill');

        if (!bar || !fill || !window.gsap) {
            return;
        }

        window.gsap.timeline({delay: delay || 0})
            .to(bar, {
                '--outline-height': '100%',
                duration: 0.35,
                ease: 'power2.out'
            })
            .to(bar, {
                '--outline-width': '100%',
                duration: 0.45,
                ease: 'power2.inOut'
            })
            .to(fill, {
                clipPath: 'inset(0 0% 0 0)',
                duration: 0.45,
                ease: 'power2.inOut'
            }, '<')
            .to(chartRevealElements(chart), {
                opacity: 1,
                duration: 0.3,
                ease: 'power2.out'
            }, '>');
    }

    function initChartReveal(charts) {
        if (!window.gsap) {
            return;
        }

        charts.forEach(prepareChartReveal);

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    revealChart(entry.target, 0);
                    observer.unobserve(entry.target);
                });
            }, {threshold: .55});

            charts.forEach(function (chart) {
                observer.observe(chart);
            });
            return;
        }

        charts.forEach(function (chart) {
            revealChart(chart, 0);
        });
    }

    function initTooltip(tooltip) {
        if (tooltip.dataset.tooltipInitialized) {
            return;
        }

        tooltip.dataset.tooltipInitialized = 'true';
        tooltip.addEventListener('mouseenter', function () {
            updateTooltipPlacement(tooltip);
        });
        tooltip.addEventListener('focus', function () {
            updateTooltipPlacement(tooltip);
        });
        tooltip.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            updateTooltipPlacement(tooltip);
            document.querySelectorAll('.compare-tooltip.is-open').forEach(function (openTooltip) {
                if (openTooltip !== tooltip) openTooltip.classList.remove('is-open');
            });
            tooltip.classList.toggle('is-open');
        });
    }

    function getTooltipHeight(tooltip) {
        const measure = document.createElement('div');
        const styles = window.getComputedStyle(tooltip);

        measure.textContent = tooltip.dataset.tooltip || '';
        measure.style.background = '#d9d7d5';
        measure.style.borderRadius = '7px';
        measure.style.boxSizing = 'border-box';
        measure.style.fontFamily = styles.fontFamily;
        measure.style.fontSize = 'calc(1.03 * var(--base-font-size))';
        measure.style.fontWeight = '500';
        measure.style.left = '0';
        measure.style.lineHeight = '1.15';
        measure.style.padding = '1rem';
        measure.style.position = 'fixed';
        measure.style.textAlign = 'left';
        measure.style.top = '0';
        measure.style.visibility = 'hidden';
        measure.style.width = `${Math.min(280, window.innerWidth - 48)}px`;
        measure.style.zIndex = '-1';

        document.body.appendChild(measure);
        const height = measure.getBoundingClientRect().height;
        measure.remove();

        return height;
    }

    function updateTooltipPlacement(tooltip) {
        const gap = 16;
        const viewportPadding = 8;
        const tooltipHeight = getTooltipHeight(tooltip);
        const rect = tooltip.getBoundingClientRect();
        const headerOffset = getVisibleHeaderOffset();

        tooltip.classList.toggle('is-below', rect.top - headerOffset < tooltipHeight + gap + viewportPadding);
    }

    function getVisibleHeaderOffset() {
        const header = document.querySelector('.site-header');
        if (!header) {
            return 0;
        }

        const styles = window.getComputedStyle(header);
        if (styles.display === 'none' || styles.visibility === 'hidden' || styles.opacity === '0') {
            return 0;
        }

        const rect = header.getBoundingClientRect();
        if (rect.height <= 0 || rect.bottom <= 0 || rect.top >= window.innerHeight) {
            return 0;
        }

        return Math.max(0, Math.min(rect.bottom, window.innerHeight));
    }

    function initCompareGraph(block) {
        const button = block.querySelector('[data-compare-toggle]');
        const charts = Array.from(block.querySelectorAll('[data-compare-chart]'));
        const currentYear = block.dataset.currentYear || '2024-2025';
        const previousYear = block.dataset.previousYear || '2023-2024';
        let previous = false;

        charts.forEach(function (chart) {
            setChartState(chart, previous, false);
        });
        initChartReveal(charts);

        button?.addEventListener('click', function () {
            previous = !previous;
            block.classList.toggle('is-previous', previous);
            button.textContent = previous ? 'Compare to current year' : 'Compare to previous year';

            charts.forEach(function (chart) {
                setChartState(chart, previous, true);
                const year = chart.querySelector('[data-compare-year]');
                if (year) year.innerHTML = previous ? previousYear : currentYear;
            });
        });

        block.querySelectorAll('.compare-tooltip').forEach(initTooltip);

        window.addEventListener('resize', function () {
            charts.forEach(function (chart) {
                setChartState(chart, previous, false);
            });
        }, {passive: true});
    }

    function preparePerformanceGraph(graph) {
        const fill = graph.querySelector('[data-performance-fill]');
        const result = graph.querySelector('[data-performance-result]');

        if (!fill || !window.gsap) {
            return;
        }

        window.gsap.set(fill, {scaleX: 0});
        if (result) {
            window.gsap.set(result, {autoAlpha: 0, y: 8});
        }
    }

    function revealPerformanceGraph(graph) {
        const fill = graph.querySelector('[data-performance-fill]');
        const result = graph.querySelector('[data-performance-result]');

        if (!fill || !window.gsap) {
            return;
        }

        const timeline = window.gsap.timeline();

        timeline.to(fill, {
            scaleX: 1,
            duration: 0.85,
            ease: 'power2.inOut'
        });

        if (result) {
            timeline.to(result, {
                autoAlpha: 1,
                y: 0,
                duration: 0.3,
                ease: 'power2.out'
            }, '>');
        }
    }

    function initPerformanceGraphs() {
        const graphs = Array.from(document.querySelectorAll('[data-performance-graph]'));

        if (!graphs.length || !window.gsap) {
            return;
        }

        graphs.forEach(preparePerformanceGraph);

        if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver(function (entries) {
                entries.forEach(function (entry) {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    revealPerformanceGraph(entry.target);
                    observer.unobserve(entry.target);
                });
            }, {threshold: 0.55});

            graphs.forEach(function (graph) {
                observer.observe(graph);
            });
            return;
        }

        graphs.forEach(revealPerformanceGraph);
    }

    window.addEventListener('load', function () {
        document.querySelectorAll('[data-block="compare-graph"]').forEach(initCompareGraph);
        document.querySelectorAll('.compare-tooltip').forEach(initTooltip);
        initPerformanceGraphs();

        document.addEventListener('click', function () {
            document.querySelectorAll('.compare-tooltip.is-open').forEach(function (tooltip) {
                tooltip.classList.remove('is-open');
            });
        });

        window.addEventListener('resize', function () {
            document.querySelectorAll('.compare-tooltip.is-open').forEach(updateTooltipPlacement);
        }, {passive: true});

        window.addEventListener('scroll', function () {
            document.querySelectorAll('.compare-tooltip.is-open').forEach(updateTooltipPlacement);
        }, {passive: true});
    });
})();
